/// <reference types="google.maps" />
/**
 * `GooglePlacesPicker` — shared Google Places Autocomplete input.
 *
 * Built on the same `useGoogleMaps` singleton loader the Places page uses (so
 * the Maps JS API is fetched at most once per session). Mirrors the
 * field-extraction logic of the legacy `GoogleAutoComplete.jsx` so the parsed
 * `{ address, city, state, country, zipcode, latitude, longitude }` shape stays
 * identical across V1/V2 — postal_code → zipcode, locality (with
 * administrative_area_level_3 / _2 fallback for cities the locality field
 * doesn't carry, e.g. some Indian metros) → city, administrative_area_level_1
 * → state, country `short_name` → country, geometry.location.lat/lng →
 * latitude / longitude.
 *
 * Renders as a styled shadcn-`Input`-look text box with an overlaid prediction
 * dropdown. Driven by the same debounced AutocompleteService → PlacesService
 * pattern as `pages/places/components/LocationSearch`. Both the Register form
 * and the Edit Profile / Contact tab consume this.
 *
 * Failure modes (`status === 'no-key' | 'error'`) degrade gracefully: the
 * input keeps working as a plain text box so users on local dev (no key) or
 * with a Maps API outage can still type a city.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react';
import { Loader2, MapPin } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useGoogleMaps } from '@/pages/places/hooks/useGoogleMaps';

/** Parsed result returned to the caller via `onSelect`. */
export interface GooglePlace {
  /** Best-effort street-address portion (without city/state/country). */
  address: string;
  city: string;
  state: string;
  /** ISO short_name, e.g. `'US'`, `'IN'`. */
  country: string;
  /** Full long_name of the country, e.g. `'United States'`. */
  country_name: string;
  zipcode: string;
  latitude: number | null;
  longitude: number | null;
  /** Composed display string (address, city, state, country). */
  location: string;
}

export interface GooglePlacesPickerProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onSelect' | 'value' | 'defaultValue'
  > {
  /** Controlled text value (e.g. the saved `city` string). */
  value?: string;
  /** Fired on every keystroke (debounced predictions are independent). */
  onChange?: (value: string) => void;
  /** Fired when the user picks a prediction and Places resolves the details. */
  onSelect?: (place: GooglePlace) => void;
  /** Wrapper className (the input itself takes `inputClassName`). */
  className?: string;
  inputClassName?: string;
}

type Prediction = google.maps.places.AutocompletePrediction;

/** Strip leading directional / municipal suffix noise from a city name. */
function stripCityNoise(name: string): string {
  return name
    .replace(/\b(West|East|North|South|Division)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strip the "Division" suffix from a state name (mirrors legacy behaviour). */
function stripStateNoise(name: string): string {
  return name.replace(/\b(Division)\b/g, '').replace(/\s+/g, ' ').trim();
}

/** Trim the trailing administrative words from a city ("City", "County", …). */
function trimCityWords(name: string): string {
  return name.replace(/\b(City|County|Metro)\b/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Parse a `PlaceResult` into the canonical `GooglePlace` shape. Pulled out of
 * the component so it can be unit-tested without rendering.
 */
export function parsePlace(place: google.maps.places.PlaceResult): GooglePlace {
  const components = place.address_components ?? [];
  const addressParts: string[] = [];
  let city = '';
  let state = '';
  let country = '';
  let countryName = '';
  let zipcode = '';

  for (const comp of components) {
    const types = comp.types ?? [];

    const isAddressPart =
      !comp.long_name.includes('Unnamed Road') &&
      !types.includes('plus_code') &&
      (types.includes('street_number') ||
        types.includes('route') ||
        types.includes('sublocality') ||
        types.includes('sublocality_level_1') ||
        types.includes('sublocality_level_2') ||
        types.includes('sublocality_level_3') ||
        types.includes('premise'));
    if (isAddressPart) addressParts.push(comp.long_name);

    if (types.includes('locality')) {
      city = comp.long_name.trim();
    }
    if (types.includes('administrative_area_level_3')) {
      city = stripCityNoise(comp.long_name);
    }
    if (!city && types.includes('administrative_area_level_2')) {
      city = stripCityNoise(comp.long_name);
    }
    if (types.includes('administrative_area_level_1')) {
      state = stripStateNoise(comp.long_name);
    }
    if (types.includes('country')) {
      country = comp.short_name;
      countryName = comp.long_name;
    }
    if (types.includes('postal_code')) {
      zipcode = comp.long_name;
    }
  }

  // Legacy fallback: if no recognised address parts, use the first component.
  if (
    addressParts.length === 0 &&
    components[0] &&
    !(components[0].types ?? []).includes('plus_code')
  ) {
    addressParts.push(components[0].long_name);
  }

  if (city) city = trimCityWords(city);

  // Don't repeat the city inside the street-address string.
  const dedupedAddressParts = Array.from(
    new Set(
      addressParts.filter(
        (a) => a.toLowerCase() !== (city || '').toLowerCase(),
      ),
    ),
  );

  // Prefer the viewport center over `geometry.location` for region-shaped
  // picks (cities, districts, states). Google's `location` is its
  // "representative point" — for some localities (e.g. "Patiala") it
  // returns a point that's actually inside a neighboring metro
  // (Chandigarh in that case), which propagates into `map_lat`/`map_lng`
  // and breaks distance-based features. The viewport is the place's
  // bounding box; its center is reliably inside the picked entity.
  // For specific addresses (street_number/premise/route) the viewport is
  // already tight around `location`, so the values agree.
  const isRegionPick = (place.types ?? []).some((t) =>
    /^(locality|administrative_area_level_\d|country|postal_code|sublocality|neighborhood)$/.test(t),
  );
  const viewport = place.geometry?.viewport;
  let latitude: number | null = null;
  let longitude: number | null = null;
  if (isRegionPick && viewport) {
    const center = viewport.getCenter();
    latitude = center.lat();
    longitude = center.lng();
  } else {
    const loc = place.geometry?.location;
    latitude = loc?.lat() ?? null;
    longitude = loc?.lng() ?? null;
  }

  // Compose the human-readable label.
  const parts: string[] = [];
  if (dedupedAddressParts.length) parts.push(dedupedAddressParts.join(', '));
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (country) parts.push(country);
  const location = Array.from(new Set(parts)).join(', ');

  return {
    address: dedupedAddressParts.join(', '),
    city,
    state,
    country,
    country_name: countryName,
    zipcode,
    latitude,
    longitude,
    location,
  };
}

/** Imperative handle so callers (e.g. RHF refs) can focus / clear the input. */
export interface GooglePlacesPickerHandle {
  focus: () => void;
  clear: () => void;
}

export const GooglePlacesPicker = forwardRef<
  GooglePlacesPickerHandle,
  GooglePlacesPickerProps
>(function GooglePlacesPicker(
  {
    value,
    onChange,
    onSelect,
    placeholder = 'Start typing a city or address…',
    className,
    inputClassName,
    disabled,
    ...rest
  },
  ref,
) {
  const { status } = useGoogleMaps();
  const ready = status === 'ready';

  const [input, setInput] = useState<string>(value ?? '');
  const [options, setOptions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const autoSvc = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesSvc = useRef<google.maps.places.PlacesService | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => {
        setInput('');
        setOptions([]);
        onChange?.('');
      },
    }),
    [onChange],
  );

  // Sync external `value` prop into our controlled input.
  useEffect(() => {
    if (value !== undefined && value !== input) setInput(value);
    // We intentionally only sync when the prop reference changes — local typing
    // updates `input` directly via the change handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Lazily create the Google services once the API is ready.
  useEffect(() => {
    if (!ready || !window.google?.maps?.places) return;
    if (!autoSvc.current) {
      autoSvc.current = new window.google.maps.places.AutocompleteService();
    }
    if (!placesSvc.current) {
      placesSvc.current = new window.google.maps.places.PlacesService(
        document.createElement('div'),
      );
    }
  }, [ready]);

  // Debounced prediction fetch.
  const fetchPredictions = useCallback((next: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!autoSvc.current || !next.trim()) {
        setOptions([]);
        return;
      }
      autoSvc.current.getPlacePredictions({ input: next }, (res) => {
        setOptions(res ?? []);
      });
    }, 350);
  }, []);

  useEffect(() => {
    if (ready) fetchPredictions(input);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [input, ready, fetchPredictions]);

  function choose(pred: Prediction) {
    setOpen(false);
    setOptions([]);
    if (!placesSvc.current) return;
    setFetching(true);
    placesSvc.current.getDetails(
      {
        placeId: pred.place_id,
        fields: ['address_components', 'geometry', 'name', 'formatted_address'],
      },
      (place, status) => {
        setFetching(false);
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place
        ) {
          return;
        }
        const parsed = parsePlace(place);
        const label = parsed.location || pred.description;
        setInput(label);
        onChange?.(label);
        onSelect?.(parsed);
      },
    );
  }

  return (
    <div className={cn('relative w-full', className)}>
      <Input
        {...rest}
        ref={inputRef}
        value={input}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          const next = e.target.value;
          setInput(next);
          setOpen(true);
          onChange?.(next);
        }}
        onFocus={() => setOpen(true)}
        // Defer the close so a click on a prediction (onMouseDown) lands first.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={cn(inputClassName)}
      />
      {fetching ? (
        <Loader2
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      ) : null}
      {open && ready && options.length > 0 ? (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          {options.map((opt) => (
            <li key={opt.place_id} role="option" aria-selected="false">
              <button
                type="button"
                // onMouseDown fires before the input's onBlur closes the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(opt);
                }}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
              >
                <MapPin
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink">
                    {opt.structured_formatting?.main_text ?? opt.description}
                  </span>
                  {opt.structured_formatting?.secondary_text ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {opt.structured_formatting.secondary_text}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
});
